"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.summarizePagePart = summarizePagePart;
exports.pagePartMayHold = pagePartMayHold;
exports.samePagePartSummary = samePagePartSummary;
exports.readPagePartSummary = readPagePartSummary;
const database_row_fingerprint_1 = require("./database-row-fingerprint");
const page_layer_index_1 = require("./page-layer-index");
const page_structure_tree_1 = require("./page-structure-tree");
/** Summarizes exactly one part, depending only on its entries. */
function summarizePagePart(entries) {
    const kinds = new Set();
    const groups = new Set();
    const ends = new Set();
    const children = new Map();
    let deletedAt;
    let pi;
    for (const entry of entries.values()) {
        if (entry.p === page_structure_tree_1.PAGE_TREE_DELETED_ID) {
            const time = Number(entry.d);
            if (Number.isFinite(time) &&
                (deletedAt === undefined || time < deletedAt))
                deletedAt = time;
        }
        if (!(0, page_structure_tree_1.isPageTreeEntry)(entry))
            continue;
        kinds.add(entry.k);
        for (const end of entry.c ?? [])
            if (end !== null && !entries.has(end))
                ends.add(end);
        if (entry.g !== undefined && !entries.has(entry.g))
            groups.add(entry.g);
        if (entry.pi !== undefined && (pi === undefined || entry.pi > pi))
            pi = entry.pi;
        const parent = entry.p;
        if (parent === null ||
            parent === page_structure_tree_1.PAGE_TREE_RECOVERED_ID ||
            entries.has(parent))
            continue;
        const previous = children.get(parent);
        children.set(parent, previous
            ? [
                previous[0] + 1,
                entry.o < previous[1] ? entry.o : previous[1],
                entry.o > previous[2] ? entry.o : previous[2],
            ]
            : [1, entry.o, entry.o]);
    }
    const { first, last } = (0, page_layer_index_1.pageCanvasLayerBounds)(entries);
    return {
        ...(entries.size
            ? { ids: (0, database_row_fingerprint_1.createDatabaseRowFingerprint)([...entries.keys()].sort()) }
            : {}),
        kinds: [...kinds].sort(),
        ...(groups.size ? { groups: [...groups].sort() } : {}),
        ...(ends.size ? { ends: [...ends].sort() } : {}),
        ...(deletedAt === undefined ? {} : { deletedAt }),
        ...(pi === undefined ? {} : { pi }),
        ...(first === null ? {} : { layers: [first, last] }),
        children: Object.fromEntries([...children.keys()].sort().map(parent => [parent, children.get(parent)])),
    };
}
/** False only when the ID definitely does not belong to this part. */
function pagePartMayHold(summary, id) {
    return (summary.ids !== undefined &&
        (0, database_row_fingerprint_1.databaseRowFingerprintMayContain)(summary.ids, (0, database_row_fingerprint_1.databaseRowFingerprintProbe)(id)));
}
function sameValues(a, b) {
    if (a === b)
        return true;
    if (a === undefined || b === undefined || a.length !== b.length)
        return false;
    for (let index = 0; index < a.length; index++) {
        if (a[index] !== b[index])
            return false;
    }
    return true;
}
/** Byte equality of fingerprints and value equality of every other summary field. */
function samePagePartSummary(a, b) {
    if (a === b)
        return true;
    if (!sameValues(a.ids, b.ids) ||
        !sameValues(a.kinds, b.kinds) ||
        !sameValues(a.groups, b.groups) ||
        !sameValues(a.ends, b.ends) ||
        a.deletedAt !== b.deletedAt ||
        a.pi !== b.pi ||
        !sameValues(a.layers, b.layers))
        return false;
    const parents = Object.keys(a.children);
    return (parents.length === Object.keys(b.children).length &&
        parents.every(parent => Object.prototype.hasOwnProperty.call(b.children, parent) &&
            sameValues(a.children[parent], b.children[parent])));
}
/** Validates and copies a persisted part summary without retaining mutable aliases. */
function readPagePartSummary(value) {
    const invalid = () => new Error('Invalid page part summary');
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        throw invalid();
    const { ids, kinds, groups, ends, deletedAt, pi, layers, children } = value;
    if ((ids !== undefined && !(0, database_row_fingerprint_1.isDatabaseRowFingerprint)(ids)) ||
        !Array.isArray(kinds) ||
        ![...kinds].every(kind => typeof kind === 'string') ||
        (groups !== undefined &&
            (!Array.isArray(groups) ||
                !groups.every((id, at) => typeof id === 'string' && (at === 0 || groups[at - 1] < id)))) ||
        (ends !== undefined &&
            (!Array.isArray(ends) ||
                ![...ends].every((id, at) => typeof id === 'string' && (at === 0 || ends[at - 1] < id)))) ||
        (deletedAt !== undefined &&
            (typeof deletedAt !== 'number' || !Number.isFinite(deletedAt))) ||
        (pi !== undefined && typeof pi !== 'string') ||
        (layers !== undefined &&
            (!Array.isArray(layers) ||
                layers.length !== 2 ||
                typeof layers[0] !== 'string' ||
                typeof layers[1] !== 'string')) ||
        typeof children !== 'object' ||
        children === null ||
        Array.isArray(children))
        throw invalid();
    const spans = Object.entries(children).map(([parent, span]) => {
        if (!Array.isArray(span) ||
            span.length !== 3 ||
            typeof span[0] !== 'number' ||
            !Number.isInteger(span[0]) ||
            span[0] < 0 ||
            typeof span[1] !== 'string' ||
            typeof span[2] !== 'string')
            throw invalid();
        return [parent, [span[0], span[1], span[2]]];
    });
    return {
        ...(ids === undefined ? {} : { ids: ids.slice() }),
        kinds: [...kinds],
        ...(groups === undefined ? {} : { groups: [...groups] }),
        ...(ends === undefined ? {} : { ends: [...ends] }),
        ...(deletedAt === undefined ? {} : { deletedAt }),
        ...(pi === undefined ? {} : { pi }),
        ...(layers === undefined
            ? {}
            : { layers: [layers[0], layers[1]] }),
        children: Object.fromEntries(spans),
    };
}
